import React, { useEffect, useState, useMemo } from 'react';
import { createRoute, redirect, useNavigate, useSearch } from '@tanstack/react-router';
import { useAuth0 } from '@auth0/auth0-react';
import { getYear, getTime, startOfDay, isBefore, differenceInMinutes } from 'date-fns';
import { Route as rootRoute } from '../../__root';
import { useUpcomingStreamsStore, DateRange } from '../../../store/upcomingStreamsStore';
import { Layout } from '@/client/layouts/layout';
import { cn } from '@/client/lib/utils';
import {
  formatDateWithConditionalYear,
  formatDateWithWeekday,
  formatDateRange,
  getWeekBounds,
  getMonthBounds,
  getNextWeekBounds,
  getNextMonthBounds,
  getLastMonthBounds,
  getTomorrow,
  toISODateString,
} from '@/client/lib/date';
import { downloadCSV } from '@/client/lib/csv';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  flexRender,
  ColumnDef,
  OnChangeFn,
} from '@tanstack/react-table';
import type { UpcomingStreamResponse } from '@shared/types';
import { upcomingStreamsColumns } from '@/client/components/tables/upcoming-streams-columns';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/client/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/client/components/ui/dropdown-menu';
import { Button } from '@/client/components/ui/button';
import { Input } from '@/client/components/ui/input';
import { Switch } from '@/client/components/ui/switch';
import { Label } from '@/client/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/client/components/ui/card';
import { CacheTimestamp } from '@/client/components/cache-timestamp';
import { RefreshCw, ChevronDown, Download } from 'lucide-react';
import { parseString, buildSearchParams } from '@/client/lib/searchParams';
import { usePersistedSearch } from '@/client/hooks/usePersistedSearch';
import { ResetFiltersButton } from '@/client/components/reset-filters-button';

const DATE_RANGE_LABELS: Record<DateRange, string> = {
  today: 'Today',
  tomorrow: 'Tomorrow',
  this_week: 'This Week (Mon-Sun)',
  next_week: 'Next Week (Mon-Sun)',
  this_month: 'This Month',
  next_month: 'Next Month',
  last_month: 'Last Month',
};

// Single day ranges just show the day name
const SINGLE_DAY_RANGES: DateRange[] = ['today', 'tomorrow'];

/**
 * Find the index of the closest upcoming event (next to start)
 * Returns the eventID of the first event that starts in the future, or null
 */
function findClosestUpcomingEventId(streams: UpcomingStreamResponse[]): string | null {
  const now = new Date();

  // Sort by start time and find first future event
  const sorted = [...streams].sort((a, b) =>
    new Date(a.startDateTimePT).getTime() - new Date(b.startDateTimePT).getTime()
  );

  for (const stream of sorted) {
    if (new Date(stream.startDateTimePT) > now) {
      return stream.eventID;
    }
  }

  return null;
}

/**
 * Time indicator component - shows current time with a yellow line
 * Similar to Apple Calendar's "now" indicator
 * Updates every 60 seconds
 */
function NowIndicator() {
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const interval = setInterval(() => {
      setNow(new Date());
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const timeStr = now.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  return (
    <div className="relative h-0 z-10 overflow-visible">
      <div className="absolute left-0 right-0 flex items-center" style={{ top: '-1px' }}>
        <div className="flex-1 h-0.5 bg-yellow-400" />
        <span className="bg-yellow-400 text-black text-xs font-bold px-1.5 py-0.5 rounded-sm shrink-0 -my-2">
          {timeStr}
        </span>
      </div>
    </div>
  );
}

/**
 * Check if this row should show the "now" indicator above it
 */
function isClosestUpcoming(eventID: string, closestEventId: string | null): boolean {
  return eventID === closestEventId;
}

/**
 * Format a date range for display in the summary card
 */
function formatDateRangeLabel(dateRange: DateRange): string {
  const now = new Date();
  const currentYear = getYear(now);

  if (dateRange === 'today') {
    return formatDateWithConditionalYear(now, currentYear);
  }

  if (dateRange === 'tomorrow') {
    return formatDateWithConditionalYear(getTomorrow(now), currentYear);
  }

  let start: Date;
  let end: Date;

  switch (dateRange) {
    case 'this_week': {
      const bounds = getWeekBounds(now);
      start = bounds.start;
      end = bounds.end;
      break;
    }
    case 'next_week': {
      const bounds = getNextWeekBounds(now);
      start = bounds.start;
      end = bounds.end;
      break;
    }
    case 'this_month': {
      const bounds = getMonthBounds(now);
      start = bounds.start;
      end = bounds.end;
      break;
    }
    case 'next_month': {
      const bounds = getNextMonthBounds(now);
      start = bounds.start;
      end = bounds.end;
      break;
    }
    case 'last_month': {
      const bounds = getLastMonthBounds(now);
      start = bounds.start;
      end = bounds.end;
      break;
    }
    default:
      return DATE_RANGE_LABELS[dateRange];
  }

  return formatDateRange(start, end, currentYear);
}

interface DayTableProps {
  dateLabel: string;
  streams: UpcomingStreamResponse[];
  columns: ColumnDef<UpcomingStreamResponse>[];
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
  onRowClick: (stream: UpcomingStreamResponse) => void;
  closestEventId: string | null;
}

function DayTable({ dateLabel, streams, columns, sorting, onSortingChange, onRowClick, closestEventId }: DayTableProps) {
  const table = useReactTable({
    data: streams,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange,
    getSortedRowModel: getSortedRowModel(),
    state: { sorting },
  });

  return (
    <div>
      <div className="flex items-center gap-3 mb-2">
        <h4 className="text-lg font-medium text-muted-foreground">{dateLabel}</h4>
        <span className="inline-flex items-center justify-center rounded-full font-mono dark:bg-gray-50 dark:text-gray-800 text-gray-50 bg-gray-800 text-sm font-bold px-2.5 py-0.5 min-w-[28px]">
          {streams.length}
        </span>
      </div>
      <div className="rounded-md border">
        <Table className="table-fixed">
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    style={{ width: header.column.columnDef.size ? `${header.column.columnDef.size}px` : undefined }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <React.Fragment key={row.id}>
                {isClosestUpcoming(row.original.eventID, closestEventId) && (
                  <tr>
                    <td colSpan={row.getVisibleCells().length} className="p-0 border-0">
                      <NowIndicator />
                    </td>
                  </tr>
                )}
                <TableRow
                  className="cursor-pointer hover:bg-muted transition-colors"
                  onClick={() => onRowClick(row.original)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      className={cell.column.id === 'startDateTimePT' ? 'whitespace-nowrap' : 'truncate'}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

type UpcomingStreamsSearch = {
  search?: string;
  range?: DateRange;
};

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: 'events/upcoming-streams',
  validateSearch: (search: Record<string, unknown>): UpcomingStreamsSearch => ({
    search: parseString(search.search),
    range: Object.keys(DATE_RANGE_LABELS).includes(search.range as string)
      ? (search.range as DateRange)
      : undefined,
  }),
  beforeLoad: async ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({ to: '/' });
    }
  },
  component: UpcomingStreamsComponent,
});

function UpcomingStreamsComponent() {
  const { getAccessTokenSilently, logout } = useAuth0();
  const navigate = useNavigate();
  const { search: searchFilter = '', range: urlRange } = useSearch({ from: '/events/upcoming-streams' });

  // Persist search params across navigation
  usePersistedSearch('/events/upcoming-streams', { search: undefined, range: undefined });

  const { upcomingStreams, dateRange, cacheInfo, loading, error, fetchUpcomingStreams } = useUpcomingStreamsStore();
  // Default sort: startDateTimePT ascending
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'startDateTimePT', desc: false }
  ]);
  const [refreshCooldown, setRefreshCooldown] = useState(false);
  const [showElapsedDays, setShowElapsedDays] = useState(false);

  // Update URL when search filter changes
  const setSearchFilter = (value: string) => {
    navigate({
      to: '/events/upcoming-streams',
      search: buildSearchParams({ search: value || undefined, range: dateRange !== 'today' ? dateRange : undefined }),
    });
  };

  const loadUpcomingStreams = async (forceRefresh = false, range?: DateRange) => {
    try {
      const token = await getAccessTokenSilently();
      await fetchUpcomingStreams(token, logout, forceRefresh, range);
    } catch (err) {
      console.error('Error fetching upcoming streams:', err);
    }
  };

  const handleRefresh = async () => {
    setRefreshCooldown(true);
    await loadUpcomingStreams(true); // Force refresh when user clicks button
    setTimeout(() => setRefreshCooldown(false), 5000);
  };

  const handleDateRangeChange = async (range: DateRange) => {
    // Update URL and fetch data
    navigate({
      to: '/events/upcoming-streams',
      search: buildSearchParams({ search: searchFilter || undefined, range: range !== 'today' ? range : undefined }),
    });
    await loadUpcomingStreams(false, range);
  };

  const handleExportCSV = () => {
    const headers = ['Client', 'Event ID', 'Title', 'Start Date/Time (PT)', 'Encoder', 'Status'];
    const rows = filteredUpcomingStreams.map(stream => [
      stream.clientName || '',
      stream.eventID || '',
      stream.eventTitle || '',
      stream.startDateTimePT ? new Date(stream.startDateTimePT).toLocaleString('en-US', {
        timeZone: 'America/Los_Angeles',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      }) : '',
      stream.encoderID || 'VOD',
      stream.status || '',
    ]);

    const filename = `upcoming-streams-${dateRange}-${toISODateString(new Date())}.csv`;
    downloadCSV(filename, headers, rows);
  };

  const handleRowClick = (stream: UpcomingStreamResponse) => {
    navigate({
      to: '/events/$eventId',
      params: { eventId: stream.eventID },
    });
  };

  // Sync URL range to store on mount (if URL has a range and it differs from store)
  useEffect(() => {
    if (urlRange && urlRange !== dateRange) {
      loadUpcomingStreams(false, urlRange);
    } else {
      loadUpcomingStreams();
    }
  }, [getAccessTokenSilently]);

  const filteredUpcomingStreams = useMemo(() => {
    if (!searchFilter) return upcomingStreams;
    const lowerFilter = searchFilter.toLowerCase();
    return upcomingStreams.filter((stream) =>
      stream.clientName?.toLowerCase().includes(lowerFilter) ||
      stream.eventTitle?.toLowerCase().includes(lowerFilter)
    );
  }, [upcomingStreams, searchFilter]);

  // Find the closest upcoming event for the "you are here" marker
  const closestEventId = useMemo(() => {
    return findClosestUpcomingEventId(filteredUpcomingStreams);
  }, [filteredUpcomingStreams]);

  // Group streams by date for multi-day views (week/month)
  const shouldGroupByDate = ['this_week', 'next_week', 'this_month', 'next_month', 'last_month'].includes(dateRange);

  const streamsByDate = useMemo(() => {
    if (!shouldGroupByDate) return null;

    const grouped: Record<string, typeof filteredUpcomingStreams> = {};
    const currentYear = getYear(new Date());
    const todayStart = startOfDay(new Date());

    filteredUpcomingStreams.forEach((stream) => {
      const date = new Date(stream.startDateTimePT);
      const dateKey = formatDateWithWeekday(date, currentYear);

      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(stream);
    });

    // Sort by date and return as array of [dateLabel, streams]
    const sorted = Object.entries(grouped).sort((a, b) => {
      const dateA = new Date(a[1][0].startDateTimePT);
      const dateB = new Date(b[1][0].startDateTimePT);
      return getTime(dateA) - getTime(dateB);
    });

    // Filter out elapsed days unless showElapsedDays is true
    if (!showElapsedDays) {
      return sorted.filter(([, streams]) => {
        const dayDate = startOfDay(new Date(streams[0].startDateTimePT));
        return !isBefore(dayDate, todayStart);
      });
    }

    return sorted;
  }, [filteredUpcomingStreams, shouldGroupByDate, showElapsedDays]);

  const table = useReactTable({
    data: filteredUpcomingStreams,
    columns: upcomingStreamsColumns,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    state: { sorting },
  });

  return (
    <Layout>
      <div>
        <section>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-2xl font-semibold">Upcoming Streams</h3>
            <CacheTimestamp cachedAt={cacheInfo?.cachedAt ?? null} />
          </div>

          {/* Summary Card */}
          {!loading && !error && (
            <div className="mb-4">
              <Card className="w-fit">
                <CardContent className="flex items-center gap-6 p-4">
                  <div>
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      {DATE_RANGE_LABELS[dateRange]}
                    </CardTitle>
                    <p className="text-xs text-muted-foreground mt-1">
                      {formatDateRangeLabel(dateRange)}
                    </p>
                  </div>
                  <div className="text-5xl font-bold tabular-nums">
                    {upcomingStreams.length}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          <div className="flex justify-end items-center gap-4 mb-4">
            {shouldGroupByDate && (
              <div className="flex items-center gap-2">
                <Switch
                  id="show-elapsed-days"
                  checked={showElapsedDays}
                  onCheckedChange={setShowElapsedDays}
                />
                <Label htmlFor="show-elapsed-days" className="text-sm text-muted-foreground cursor-pointer">
                  Show elapsed days
                </Label>
              </div>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  Date Range: {DATE_RANGE_LABELS[dateRange]}
                  <ChevronDown className="ml-2 h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleDateRangeChange('today')}>
                  Today
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleDateRangeChange('tomorrow')}>
                  Tomorrow
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => handleDateRangeChange('this_week')}>
                  This Week (Mon-Sun)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleDateRangeChange('next_week')}>
                  Next Week (Mon-Sun)
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => handleDateRangeChange('this_month')}>
                  This Month
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleDateRangeChange('next_month')}>
                  Next Month
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => handleDateRangeChange('last_month')}>
                  Last Month
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Input
              placeholder="Filter by client or title..."
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              className="max-w-xs"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={loading || refreshCooldown}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Force Refresh
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportCSV}
              disabled={filteredUpcomingStreams.length === 0}
            >
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
            <ResetFiltersButton
              routePath="/events/upcoming-streams"
              defaultValues={{ search: undefined, range: undefined }}
            />
          </div>

          {loading && <p>Loading upcoming streams...</p>}

          {error && (
            <div className="p-4 bg-red-100 border-l-4 border-red-600 rounded mt-4">
              <p className="m-0 text-red-800 font-medium">Error: {error}</p>
            </div>
          )}

          {!loading && !error && upcomingStreams.length === 0 && (
            <p>No upcoming streams found for {DATE_RANGE_LABELS[dateRange].toLowerCase()}.</p>
          )}

          {!loading && !error && upcomingStreams.length > 0 && (
            shouldGroupByDate && streamsByDate ? (
              <div className="space-y-6">
                {streamsByDate.map(([dateLabel, streams]) => (
                  <DayTable
                    key={dateLabel}
                    dateLabel={dateLabel}
                    streams={streams}
                    columns={upcomingStreamsColumns}
                    sorting={sorting}
                    onSortingChange={setSorting}
                    onRowClick={handleRowClick}
                    closestEventId={closestEventId}
                  />
                ))}
              </div>
            ) : (
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <TableRow key={headerGroup.id}>
                        {headerGroup.headers.map((header) => (
                          <TableHead key={header.id}>
                            {header.isPlaceholder
                              ? null
                              : flexRender(header.column.columnDef.header, header.getContext())}
                          </TableHead>
                        ))}
                      </TableRow>
                    ))}
                  </TableHeader>
                  <TableBody>
                    {table.getRowModel().rows.map((row) => (
                      <React.Fragment key={row.id}>
                        {isClosestUpcoming(row.original.eventID, closestEventId) && (
                          <tr>
                            <td colSpan={row.getVisibleCells().length} className="p-0 border-0">
                              <NowIndicator />
                            </td>
                          </tr>
                        )}
                        <TableRow
                          className="cursor-pointer hover:bg-muted transition-colors"
                          onClick={() => handleRowClick(row.original)}
                        >
                          {row.getVisibleCells().map((cell) => (
                            <TableCell key={cell.id}>
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </TableCell>
                          ))}
                        </TableRow>
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )
          )}
        </section>
      </div>
    </Layout>
  );
}
